import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import { CopyTreeComponent } from "./copy-tree/copy-tree.component";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { AsyncPipe } from "@angular/common";
import { PublicationFacade } from "../../store/facade/publication.facade";
import { MatDialog } from "@angular/material/dialog";
import { filter, map, Subscription } from "rxjs";
import { SelectPublicationDialogComponent } from "../layout/dialogs/select-publication-dialog/select-publication-dialog.component";
import { TreeNodeModel } from "../../interfaces/tree-node-model";
import {
    Publication,
    PublicationModel,
} from "../../interfaces/publication-model";
import { SectionService } from "../../services/section.service";
import { v4 as uuidv4 } from "uuid";

enum KindOfLevel {
    SECTION = "s",
    SECTION_RESOURCES = "r",
}

@Component({
    standalone: true,
    selector: "gwo-copy",
    imports: [CopyTreeComponent, MatButton, MatIcon, AsyncPipe],
    templateUrl: "./copy.component.html",
    styleUrl: "./copy.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyComponent implements OnDestroy, OnInit {
    readonly publicationFacade = inject(PublicationFacade);

    readonly sectionService = inject(SectionService);

    readonly dialog = inject(MatDialog);

    private readonly cdr = inject(ChangeDetectorRef);

    readonly donorPublication$ = this.publicationFacade.donorPublication$;

    readonly publication$ = this.publicationFacade.publication$;

    readonly subscription: Subscription = new Subscription();

    readonly isSaving$ = this.publicationFacade.isSaving$;

    readonly saveError$ = this.publicationFacade.saveError$;

    @ViewChild("leftTree") donorTreeComponent!: CopyTreeComponent;

    @ViewChild("rightTree") receiverTreeComponent!: CopyTreeComponent;

    ngOnInit(): void {
        this.publicationFacade.clearPublication();
    }

    onAddLeftTree(): void {
        const dialogRef = this.dialog.open(SelectPublicationDialogComponent, {
            maxWidth: "100%",
            width: "70%",
            autoFocus: false,
        });

        this.subscription.add(
            dialogRef
                .afterClosed()
                .pipe(
                    filter((id) => !!id),
                    map((id: string) =>
                        this.publicationFacade.getDonorPublication(id),
                    ),
                )
                .subscribe(),
        );
    }

    onAddRightTree(): void {
        const dialogRef = this.dialog.open(SelectPublicationDialogComponent, {
            maxWidth: "100%",
            width: "70%",
            autoFocus: false,
        });

        this.subscription.add(
            dialogRef
                .afterClosed()
                .pipe(
                    filter((id) => !!id),
                    map((id: string) =>
                        this.publicationFacade.getPublication(id),
                    ),
                )
                .subscribe(),
        );
    }

    onRemoveRightNode(): void {
        if (!this.receiverTreeComponent?.publication()) return;
        const receiverPathsSet = new Set(
            this.receiverTreeComponent.clickedElements.selectedKeys,
        );
        const receiverTree: PublicationModel = JSON.parse(
            JSON.stringify(this.receiverTreeComponent.publication()),
        );

        if (receiverTree.sections)
            receiverTree.sections = this.removeSelectedIdsFromSection(
                receiverTree.sections,
                receiverPathsSet,
                "",
            );

        this.publicationFacade.updatePublication(receiverTree);
        this.receiverTreeComponent.clickedElements.clear();
    }

    private remapNodeIds(
        node: TreeNodeModel,
        newParentId: string | number,
    ): TreeNodeModel {
        const newId = uuidv4();
        const remapped: TreeNodeModel = {
            ...node,
            id: newId,
            parentSection: newParentId,
        };

        if (remapped.sections && remapped.sections.length) {
            remapped.sections = remapped.sections.map((child) =>
                this.remapNodeIds(child as TreeNodeModel, newId),
            );
        }

        if (remapped.sectionResources && remapped.sectionResources.length) {
            remapped.sectionResources = remapped.sectionResources.map((res) => {
                const resCopy = res as TreeNodeModel;
                return {
                    ...resCopy,
                    id: uuidv4(),
                    parentSection: newId,
                    resource: resCopy.resource
                        ? { ...resCopy.resource, id: uuidv4() }
                        : resCopy.resource,
                } as TreeNodeModel;
            });
        }

        return remapped;
    }

    /*** @description
     * Main function
     * */
    onNodeCopy(): void {
        let receiverTree: PublicationModel;
        if (!this.receiverTreeComponent?.publication()) {
            receiverTree = new Publication();
        } else {
            receiverTree = JSON.parse(
                JSON.stringify(this.receiverTreeComponent.publication()),
            );
        }

        const donorTree = JSON.parse(
            JSON.stringify(this.donorTreeComponent.publication()),
        );
        const donorPathKeys = new Set(
            this.donorTreeComponent.clickedElements.selectedKeys,
        );

        const receiverPaths =
            this.receiverTreeComponent.clickedElements.selectedKeys;

        if (
            (!donorTree?.sections && !donorTree?.sectionResources) ||
            !donorPathKeys.size
        )
            return;

        const nodesToCopy: TreeNodeModel[] = this.getNodesToCopyByPath(
            donorTree.sections || [],
            donorPathKeys,
            "",
        );

        /** @description
         * Tries to copy each donor nodes to selected receiver nodes.
         * There's some unexpected behavior when is selected sections can copy to another section with exercises.
         * In the whole app it blocked, but here I decide to leave this.
         *
         * There is 2 reasons for that:
         * 1. There is not existing mechanism to check difference between exercise and section and automatically separate it.
         * 2. User should independently decide what exactly he will to copy.
         *
         * If in future very important to separate sections and exercises need to add validation what it is to each selected element in tree.
         * */

        if (receiverPaths.length) {
            for (const receiverPath of receiverPaths) {
                const [receiverLevel, receiverCopy] =
                    this.getSectionLinkFromPath(receiverTree, receiverPath);

                if (receiverLevel.sectionResources?.length) {
                    nodesToCopy.forEach((node) =>
                        receiverLevel.sectionResources!.push(
                            this.remapNodeIds(node, receiverLevel.id),
                        ),
                    );
                } else {
                    if (!receiverLevel.sections) receiverLevel.sections = [];
                    nodesToCopy.forEach((node) =>
                        receiverLevel.sections!.push(
                            this.remapNodeIds(node, receiverLevel.id),
                        ),
                    );
                }
                receiverTree = receiverCopy;
            }
        } else {
            const nodesToCopyWithUuid = nodesToCopy.map((node) =>
                this.remapNodeIds(node, receiverTree.id ?? 0),
            );
            receiverTree.sections = receiverTree.sections
                ? [...receiverTree.sections, ...nodesToCopyWithUuid]
                : nodesToCopyWithUuid;
        }

        this.receiverTreeComponent.getExpandedKeys();

        const receiverPathsToExpand = new Set(receiverPaths);
        this.receiverTreeComponent.addExpandedKeys(receiverPathsToExpand);

        this.publicationFacade.updatePublication(receiverTree);

        this.donorTreeComponent.clearSelection();
        this.receiverTreeComponent.clearSelection();
        this.cdr.markForCheck();
    }

    private getNodesToCopyByPath(
        nodes: TreeNodeModel[],
        donorPathKeys: Set<string>,
        parentPath: string,
        kind:
            | KindOfLevel.SECTION
            | KindOfLevel.SECTION_RESOURCES = KindOfLevel.SECTION,
    ): TreeNodeModel[] {
        const selectedNodes: TreeNodeModel[] = [];

        nodes.forEach((node, index) => {
            const path = `${parentPath}/${kind}:${index}`;

            if (donorPathKeys.has(path)) {
                const copiedNode = this.processSelectedNode(
                    node,
                    donorPathKeys,
                    path,
                );
                selectedNodes.push(copiedNode);
            } else {
                const childNodes = this.processUnselectedNode(
                    node,
                    donorPathKeys,
                    path,
                );
                selectedNodes.push(...childNodes);
            }
        });

        return selectedNodes;
    }

    private processSelectedNode(
        node: TreeNodeModel,
        donorPathKeys: Set<string>,
        path: string,
    ): TreeNodeModel {
        const nodeCopy: TreeNodeModel = JSON.parse(JSON.stringify(node));

        if (nodeCopy.sections && nodeCopy.sections.length) {
            nodeCopy.sections = this.processNodeSections(
                nodeCopy.sections,
                donorPathKeys,
                path,
            );
        }

        if (nodeCopy.sectionResources && nodeCopy.sectionResources.length) {
            nodeCopy.sectionResources = this.processNodeResources(
                nodeCopy.sectionResources,
                donorPathKeys,
                path,
            );
        }

        return nodeCopy;
    }

    private processUnselectedNode(
        node: TreeNodeModel,
        donorPathKeys: Set<string>,
        path: string,
    ): TreeNodeModel[] {
        const selectedNodes: TreeNodeModel[] = [];

        if (node.sections && node.sections.length) {
            selectedNodes.push(
                ...this.getNodesToCopyByPath(
                    node.sections,
                    donorPathKeys,
                    path,
                    KindOfLevel.SECTION,
                ),
            );
        }

        if (node.sectionResources && node.sectionResources.length) {
            const selectedResources = this.getNodesToCopyByPath(
                node.sectionResources as TreeNodeModel[],
                donorPathKeys,
                path,
                KindOfLevel.SECTION_RESOURCES,
            );
            selectedNodes.push(...selectedResources);
        }

        return selectedNodes;
    }

    private processNodeSections(
        sections: TreeNodeModel[],
        donorPathKeys: Set<string>,
        path: string,
    ): TreeNodeModel[] {
        return this.getNodesToCopyByPath(
            sections,
            donorPathKeys,
            path,
            KindOfLevel.SECTION,
        );
    }

    private processNodeResources(
        sectionResources: TreeNodeModel[],
        donorPathKeys: Set<string>,
        path: string,
    ): TreeNodeModel[] {
        return this.getNodesToCopyByPath(
            sectionResources as TreeNodeModel[],
            donorPathKeys,
            path,
            KindOfLevel.SECTION_RESOURCES,
        );
    }

    private getSectionLinkFromPath(
        publication: PublicationModel,
        path: string,
    ): [TreeNodeModel, PublicationModel] {
        const pubCopy: PublicationModel = JSON.parse(
            JSON.stringify(publication),
        );
        let level: PublicationModel | TreeNodeModel = pubCopy;
        const tokens = path
            .split("/")
            .filter((t) => t.length)
            .map((t) => t.trim());
        for (const token of tokens) {
            const [kind, idxStr] = token.split(":");
            const idx = Number(idxStr);
            if (kind === KindOfLevel.SECTION) {
                level = level.sections?.[idx] ?? level;
            } else if (kind === KindOfLevel.SECTION_RESOURCES) {
                break;
            }
        }
        return [level as TreeNodeModel, pubCopy];
    }

    private removeSelectedIdsFromSection(
        nodes: TreeNodeModel[],
        selectedPaths: Set<string>,
        parentPath: string,
        kind:
            | KindOfLevel.SECTION
            | KindOfLevel.SECTION_RESOURCES = KindOfLevel.SECTION,
    ): TreeNodeModel[] {
        const result: TreeNodeModel[] = [];
        nodes.forEach((node, index) => {
            const path = `${parentPath}/${kind}:${index}`;
            if (selectedPaths.has(path)) {
                return;
            }
            const nodeCopy: TreeNodeModel = JSON.parse(JSON.stringify(node));
            if (nodeCopy.sections && nodeCopy.sections.length) {
                nodeCopy.sections = this.removeSelectedIdsFromSection(
                    nodeCopy.sections,
                    selectedPaths,
                    path,
                    KindOfLevel.SECTION,
                );
            }
            if (nodeCopy.sectionResources && nodeCopy.sectionResources.length) {
                nodeCopy.sectionResources = this.removeSelectedIdsFromSection(
                    nodeCopy.sectionResources as TreeNodeModel[],
                    selectedPaths,
                    path,
                    KindOfLevel.SECTION_RESOURCES,
                );
            }
            result.push(nodeCopy);
        });
        return result;
    }

    onSave(): void {
        if (!this.receiverTreeComponent.publication()) return;

        this.publicationFacade.savePublication(
            this.receiverTreeComponent.publication()!,
            false,
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
